import React, { useState } from 'react';
import axios from 'axios';
import { SITE_CONTENT } from '../../data/siteContent';
import { Mail, Send, Heart, Check, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contacto() {
  const { title, description, subjects } = SITE_CONTENT.contacto;
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast({ title: 'Faltan datos', description: 'Completa todos los campos para enviarme tu mensaje.' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 30000 });
      setSent(true);
      toast({
        title: 'Mensaje enviado, pronto responderé 😉😊',
        description: ''
      });
      setForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No pudimos enviar tu mensaje. Inténtalo en un momento.';
      toast({ title: 'Algo falló', description: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="w-full flex flex-col items-center">
      <div className="w-full max-w-2xl mx-auto">
        <h2 className="text-center font-serif-display text-[1.85rem] md:text-[2.1rem] text-[#3f2e22] flex items-center justify-center gap-3">
          <Mail className="w-5 h-5 text-[#c28fb4]" strokeWidth={1.6} />
          {title}
        </h2>
        <p className="mt-2 text-center text-[#6b5440] font-body max-w-lg mx-auto">{description}</p>

        <form
          onSubmit={submit}
          className="mt-8 rounded-[26px] bg-[#fbeed7]/80 border border-[#ead6b4]/70 px-6 md:px-10 py-8 shadow-[0_14px_40px_-20px_rgba(120,80,60,0.25)] space-y-5"
        >
          <div>
            <label className="block text-[0.85rem] text-[#4b3a2d] font-medium mb-2">Nombre</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Tu nombre"
              className="w-full rounded-full px-5 py-3 bg-[#fdf6e8] border border-[#ead6b4] text-[#4b3a2d] placeholder:text-[#bfa58b] font-body outline-none transition-[box-shadow,border-color] duration-200 focus:border-[#b89dd4] focus:shadow-[0_0_0_4px_rgba(184,157,212,0.18)]"
            />
          </div>
          <div>
            <label className="block text-[0.85rem] text-[#4b3a2d] font-medium mb-2">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="tu@email.com"
              className="w-full rounded-full px-5 py-3 bg-[#fdf6e8] border border-[#ead6b4] text-[#4b3a2d] placeholder:text-[#bfa58b] font-body outline-none transition-[box-shadow,border-color] duration-200 focus:border-[#b89dd4] focus:shadow-[0_0_0_4px_rgba(184,157,212,0.18)]"
            />
          </div>
          <div>
            <label className="block text-[0.85rem] text-[#4b3a2d] font-medium mb-2">Asunto</label>
            <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
              <SelectTrigger className="w-full rounded-full px-5 py-6 bg-[#fdf6e8] border border-[#ead6b4] text-[#4b3a2d] font-body focus:ring-0 focus:ring-offset-0 data-[placeholder]:text-[#bfa58b]">
                <SelectValue placeholder="Selecciona un asunto" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-[#ead6b4] bg-[#fdf6e8]">
                {subjects.map((s) => (
                  <SelectItem key={s} value={s} className="text-[#4b3a2d] focus:bg-[#f6e6d4]">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[0.85rem] text-[#4b3a2d] font-medium mb-2">Mensaje</label>
            <textarea
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Escríbeme tu mensaje..."
              className="w-full rounded-[22px] px-5 py-3 bg-[#fdf6e8] border border-[#ead6b4] text-[#4b3a2d] placeholder:text-[#bfa58b] font-body outline-none resize-none transition-[box-shadow,border-color] duration-200 focus:border-[#b89dd4] focus:shadow-[0_0_0_4px_rgba(184,157,212,0.18)]"
            />
          </div>

          <button
            type="submit"
            disabled={sent || loading}
            className="w-full rounded-full py-3 bg-gradient-to-r from-[#cdb2e0] to-[#b89dd4] text-white font-medium tracking-wide inline-flex items-center justify-center gap-2 shadow-[0_10px_22px_-10px_rgba(155,120,190,0.6)] transition-[transform,opacity] duration-300 hover:-translate-y-0.5 disabled:opacity-80"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
            ) : sent ? (
              <><Check className="w-4 h-4" /> Enviado con luz</>
            ) : (
              <><Send className="w-4 h-4" /> Enviar mensaje</>
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-[#a27a57] font-body text-[0.85rem] inline-flex items-center gap-2 justify-center w-full">
          <Heart className="w-3.5 h-3.5 text-[#c28fb4]" /> Respondo cada mensaje, besos.
        </p>
      </div>
      <Toaster />
    </section>
  );
}
